import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { Contract } from '../api/generated/types.gen';
import { toCents } from '../format';
import { level } from '../market';

const AROUND = 3;

/**
 * A strike is still in play when both sides are quoted inside 2¢–98¢. A one-sided or empty book on a far strike
 * (bid 0¢, ask 53¢ on a thin coin) is not, so it cannot stretch the chart away from spot.
 */
export const isLive = (c: Contract) => { const b = toCents(c.yes_bid), a = toCents(c.yes_ask); return a != null && b != null && b >= 2 && a <= 98; };

/** Your position or open order on a strike, tagged on its chip ("+25 YES", "BUY 20@38"). */
export type StrikeMark = { kind: 'yes' | 'no' | 'order'; label: string };

/**
 * The strike row under the chart, at every width (plan 025): every live strike (or range bucket), any strike you hold
 * or have an order on, and a few either side of the selection, scrolled sideways. ← → step through all strikes;
 * "All" lists every one.
 */
export function StrikeChips({ contracts, selected, onSelect, range = false, onRange, marks, status }: {
  contracts: Contract[]; selected: string | null; onSelect: (c: Contract) => void; range?: boolean; onRange?: (range: boolean) => void;
  marks?: ReadonlyMap<string, StrikeMark>;
  /** "Back to current hour" or "Opening the next hour…", shown at the end of the row. */
  status?: ReactNode;
}) {
  const row = useRef<HTMLDivElement>(null);
  const keyed = useRef(false);
  const [listOpen, setListOpen] = useState(false);
  const sorted = [...contracts].sort((a, b) => Number(a.strike) - Number(b.strike));
  const at = sorted.findIndex(c => c.ticker === selected);
  const shown = sorted.filter((c, i) => isLive(c) || marks?.has(c.ticker) || (at >= 0 && Math.abs(i - at) <= AROUND));
  useEffect(() => {
    const chip = row.current?.querySelector<HTMLButtonElement>('[aria-pressed=true]');
    chip?.scrollIntoView({ inline: 'center', block: 'nearest' });
    if (keyed.current) { keyed.current = false; chip?.focus(); }
  }, [selected]);
  const step = (direction: 1 | -1) => {
    const next = sorted[at < 0 ? 0 : at + direction];
    if (next) { keyed.current = true; onSelect(next); }
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); step(-1); }
  };
  const noun = range ? 'contracts' : 'strikes';
  // The row's end sits beside the strikes on desktop and after them on phones, where width is scarce (plan 028).
  const end = (where: 'beside' | 'after') => <span className={`chips-end ${where}`}>{status}<button className="chips-all" onClick={() => setListOpen(true)} disabled={!sorted.length}>All {sorted.length} {noun}</button></span>;
  return <>
    <div className="strike-row" role="group" data-testid="strike-row" onKeyDown={onKeyDown}
      aria-label={`${range ? 'Range buckets' : 'Strikes'}. Use the arrow keys to change ${range ? 'bucket' : 'strike'}.`}>
      {onRange && <div className="family" role="group" aria-label="Contract family">
        <button aria-pressed={!range} onClick={() => onRange(false)}>Above / below</button>
        <button aria-pressed={range} onClick={() => onRange(true)}>Range</button>
      </div>}
      <div className="chips" ref={row}>
      {!sorted.length && <span className="chips-empty">No active hourly {range ? 'range buckets' : 'contracts'}. The next event appears once Kalshi lists it.</span>}
      {shown.map(c => {
        const mark = marks?.get(c.ticker);
        return <button key={c.ticker} data-ticker={c.ticker} data-strike={c.strike} aria-pressed={c.ticker === selected} className={isLive(c) ? undefined : 'inactive'} onClick={() => onSelect(c)}
          aria-label={`${range ? `${c.label}, both ends included` : c.subtitle}. YES ask ${toCents(c.yes_ask) ?? '—'}¢, NO ask ${toCents(c.no_ask) ?? '—'}¢`}>
          {range ? c.label : `≥${level(c)}`}
          <small><span className="yes-px">YES {toCents(c.yes_ask) ?? '—'}¢</span><span className="no-px"> · NO {toCents(c.no_ask) ?? '—'}¢</span></small>
          {mark && <span className={`mark ${mark.kind}`} data-testid="strike-mark">{mark.label}</span>}
        </button>;
      })}
      {end('after')}
      </div>
      {end('beside')}
    </div>
    {listOpen && <StrikeList contracts={sorted} selected={selected} range={range} onSelect={c => { onSelect(c); setListOpen(false); }} onClose={() => setListOpen(false)}/>}
  </>;
}

function StrikeList({ contracts, selected, range, onSelect, onClose }: { contracts: Contract[]; selected: string | null; range: boolean; onSelect: (c: Contract) => void; onClose: () => void }) {
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => { scroller.current?.querySelector('[aria-selected=true]')?.scrollIntoView({ block: 'center' }); }, []);
  const rows = [...contracts].reverse();
  return <div className="strike-list" role="dialog" aria-label={range ? 'All range contracts' : 'All strikes'} onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
    <div className="panel-head"><h2>{range ? 'All range contracts' : 'All strikes'}</h2><button className="tool" onClick={onClose} autoFocus>Close</button></div>
    {range && <p className="faint list-note">Each bucket includes both ends. Prices are market asks, not a probability distribution.</p>}
    <div className="scroll" ref={scroller}><table><thead><tr><th>{range ? 'Range' : 'Strike ≥'}</th><th>YES bid/ask</th><th>NO bid/ask</th></tr></thead>
      <tbody>{rows.map(c => <tr key={c.ticker} aria-selected={c.ticker === selected} onClick={() => onSelect(c)}>
        <td><button onClick={e => { e.stopPropagation(); onSelect(c); }}>{range ? c.label : level(c)}</button></td>
        <td><span className="faint">{toCents(c.yes_bid) ?? '—'}</span> <span className="up">{toCents(c.yes_ask) ?? '—'}</span></td>
        <td><span className="faint">{toCents(c.no_bid) ?? '—'}</span> <span className="down">{toCents(c.no_ask) ?? '—'}</span></td>
      </tr>)}</tbody></table></div>
  </div>;
}
